import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import _isNumber from 'lodash/isNumber';
import _pick from 'lodash/pick';
import _filter from 'lodash/filter';

import Button from '@material-ui/core/Button';

import { AppContext } from '../../AppProvider';
import CheckboxInput from '../../components/Global/Inputs/Checkbox';
import { Table as DvExTable } from '@devexpress/dx-react-grid-material-ui';
import Table from '../../components/Global/Table/Table';
import CarrierRelationshipMetadata from './CarrierRelationship.metadata';
import ErrorNotification from '../../components/common/ErrorNotification';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import { DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';

import * as Queries from '../../graphQL/queries/carrierRelationship';
import { previewEPro as previewEProQuery } from '../../graphQL/queries/ePro';
import { deleteCarrier } from '../../graphQL/mutations/carrierRelationship';

import CarrierRelationshipIndexStyles from './__styles__/CarrierRelationshipIndex.styles';

import CarrierRelationshipEProFormModal from '../../components/carrierRelationship/EProForm';

export class CarrierRelationshipIndex extends Component {
  state = {
    rowsSelected: [],
    items: [],
    loading: true,
    error: null,
    errorsOnMutation: null,
    page: 0,
    rowsPerPage: localStorage.getItem('PropelRowsPerPage')
      ? parseInt(localStorage.getItem('PropelRowsPerPage'), 10)
      : DEFAULT_ROWS_PER_PAGE,
    totalCount: 0,
    deleting: false,
    displayInactive: false,
    openEProForm: false,
    isSubmitting: false,
    submitError: null,
    eProSample: null
  };

  async componentDidMount() {
    await this._getList();
  }

  _getList = async () => {
    const { client: ApolloClient, t } = this.props;
    const { currentOrgId } = this.context.globalProps;
    const { page, rowsPerPage, displayInactive: includeExpired } = this.state;
    const offset = page * rowsPerPage;
    try {
      const {
        data: {
          carrier: {
            paged: { results: items, totalCount }
          }
        }
      } = await ApolloClient.query({
        query: Queries.paged,
        variables: {
          orgId: currentOrgId,
          limit: rowsPerPage,
          offset,
          includeExpired
        },
        fetchPolicy: 'network-only'
      });

      this.setState({
        items: CarrierRelationshipMetadata.formatData(items, t),
        totalCount: _isNumber(totalCount) ? totalCount : this.state.totalCount
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  _handleRowClick = row => {
    const { history } = this.props;
    history.push(`/carrier-relationships/${row.id}`);
  };

  _handleSelectRow = rowsSelected => this.setState({ rowsSelected });

  _handleDelete = async () => {
    this.setState({ deleting: true });
    const { client: ApolloClient } = this.props;

    const { items, rowsSelected } = this.state;
    const itemsSelected = _filter(items, (item, index) =>
      rowsSelected.includes(index)
    );
    const itemsIdSelected = itemsSelected.map(item => item.id);
    try {
      await Promise.all(
        itemsIdSelected.map(
          async itemId =>
            await ApolloClient.mutate({
              mutation: deleteCarrier,
              variables: { carrierId: parseInt(itemId) }
            })
        )
      );
      await this._getList();
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ deleting: false, rowsSelected: [] });
    }
  };

  _handleDisplayInactive = () => {
    this.setState(
      { displayInactive: !this.state.displayInactive, page: 0, loading: true },
      this._getList
    );
  };

  _changePage = page => {
    this.setState({ page, loading: true }, this._getList);
  };

  _changeRowsPerPage = rowsPerPage => {
    localStorage.setItem('PropelRowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
    const rows = rowsPerPage === 0 ? DEFAULT_ROWS_PER_PAGE : rowsPerPage;
    this.setState({ rowsPerPage: rows, page: 0, loading: true }, this._getList);
  };

  _handleGenerateSampleEProNumbers = async values => {
    const { client: ApolloClient } = this.props;

    const eProParams = _pick(values, [
      'sequenceStart',
      'format',
      'checkDigitTypeId'
    ]);

    this.setState({ isSubmitting: true });

    try {
      const {
        data: {
          ePro: { previewEPro: sample }
        }
      } = await ApolloClient.query({
        query: previewEProQuery,
        variables: {
          previewEPro: {
            ...eProParams,
            numberToReturn: 5
          }
        },
        fetchPolicy: 'network-only'
      });

      this.setState({
        eProSample: sample
      });
    } catch (submitError) {
      this.setState({
        submitError
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _handleOpenSampleEProForm = () =>
    this.setState({ openEProForm: true, eProSample: null });

  _handleCloseNewEProForm = () => this.setState({ openEProForm: false });

  render() {
    const { t, classes } = this.props;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('Carrier Relationships')}
          </h1>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="outlined"
            color="primary"
            onClick={this._handleOpenSampleEProForm}
          >
            {t('Generate E-Pro Sample')}
          </Button>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="contained"
            color="primary"
            onClick={this._handleAdd}
            component={Link}
            to="/carrier-relationships/new"
          >
            {t('ADD Relationship')}
          </Button>
        </div>
        <div>
          <Table
            name={t('Relationships')}
            columns={CarrierRelationshipMetadata.columns({ t })}
            data={this.state.displayInactive ? this.state.items:  this.state.items.filter(x => x.status === 'Active')}
            rowComponent={props => (
              <DvExTable.Row
                {...props}
                className={classes.tableRow}
                onClick={() => this._handleRowClick(props.row)}
              />
            )}
            page={this.state.page}
            rowsPerPage={this.state.rowsPerPage}
            totalCount={this.state.totalCount}
            loading={this.state.loading}
            withDownload={false}
            withColumnChooser={false}
            withSelection={true}
            withServerPaging={true}
            rowsSelected={this.state.rowsSelected}
            onSelectRow={this._handleSelectRow}
            onDelete={this._handleDelete}
            deleting={this.state.deleting}
            filterToolbar={
              <CheckboxInput
                label={t('Show Inactive')}
                name="activeToggle"
                onChange={this._handleDisplayInactive}
                value={this.state.displayInactive}
                classes={{
                  root: classnames(classes.noMargin, classes.activateToggle),
                  label: classes.activateToggleLabel
                }}
                dataTest={'active-toggle'}
              />
            }
            onChangePage={this._changePage}
            onChangePageRowsPerPage={this._changeRowsPerPage}
          />
        </div>
        {this.state.openEProForm && (
          <CarrierRelationshipEProFormModal
            onClose={this._handleCloseNewEProForm}
            onSave={this._handleGenerateSampleEProNumbers}
            isSubmitting={this.state.isSubmitting}
            sampleGenarated={this.state.eProSample}
          />
        )}
        {this.state.submitError && (
          <ErrorNotification message={this.state.submitError} />
        )}
      </div>
    );
  }
}

CarrierRelationshipIndex.contextType = AppContext;
const CarrierRelationshipIndexWithTranslation = withNamespaces()(
  CarrierRelationshipIndex
);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    CarrierRelationshipIndexStyles
  )
)(withApollo(CarrierRelationshipIndexWithTranslation));
